import axios from "axios";
import { showToast } from '@jobber/components/Toast';
import { goToJobberSettings, isJobberAuthRequired } from '../utils/jobber-auth-navigation';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api/`,
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    // Add any additional headers or auth tokens here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => {
    // Handle successful responses
    if (response.data?.message && response.config.method !== "get") {
      showToast({
        message: response.data.message,
        variation: 'success'
      });
    }

    return response;
  },
  (error) => {
    // Network or timeout error
    if (!error.response) {
      error.message = "Network error. Please try again later.";
      return Promise.reject(error);
    }

    const jobberAuthRequired = isJobberAuthRequired(error.response?.data);
    const errorMessage = error.response?.data?.error || error.response?.data?.message || "An unexpected error occurred";
    const status = error.response?.status;
    // Handle based on status code
    switch (status) {
      case 401:
        error.message = errorMessage || "Session expired. Please log in again.";
        // Don't redirect for session-check calls — AuthProvider handles those
        if (!error.response?.config?.url?.includes('user/session')) {
          window.location.href = "/login";
        }
        break;
      case 424:
        error.message = jobberAuthRequired
          ? errorMessage || "Please connect Jobber again to continue."
          : errorMessage;
        if (jobberAuthRequired) goToJobberSettings();
        break;
      case 403:
        error.message = errorMessage || "You do not have permission.";
        break;
      case 422:
        error.message = errorMessage || "Validation failed.";
        break;
      case 500:
        error.message = jobberAuthRequired
          ? errorMessage || "Please connect Jobber again to continue."
          : "Server error. Please try again later.";
        if (jobberAuthRequired) goToJobberSettings();
        break;
      default:
        error.message = errorMessage;
        break;
    }
    // Show error toast for GET failures, but not for session-check calls (handled by AuthProvider)
    const isSessionCheck = error.response?.config?.url?.includes('user/session');
    if (error.response.config.method === "get" && !isSessionCheck && !jobberAuthRequired) {
      showToast({
        message: error.message,
        variation: 'error'
      });
    }
    return Promise.reject(error);
  },
);

export async function apiGet(url, config = {}) {
  const response = await api.get(url, config);
  return response.data;
}

export async function apiPost(
  url,
  data,
  config,
) {
  const response = await api.post(url, data, config);
  return response.data;
}

export async function apiPatch(
  url,
  data,
  config,
) {
  const response = await api.patch(url, data, config);
  return response.data;
}

export async function apiPut(
  url,
  data,
  config,
) {
  const response = await api.put(url, data, config);
  return response.data;
}

export async function apiDelete(url, config) {
  const response = await api.delete(url, config);
  return response.data;
}

export default api;
